import { LitElement, html, css } from 'lit';
import { money as fmtMoney } from '../../format';
import { t } from '../../i18n';
import { customElement, state } from 'lit/decorators.js';
import { api, ApiError } from '../../api';
import { store } from '../../store';
import { showToast } from '../../components/ui/toast';

type Period = 'month' | 'last' | 'year' | 'all';

interface Summary {
  primary_currency: string; income: number; expense: number; net: number;
  outstanding: number; outstanding_indicative: boolean;
  by_currency: { currency: string; income: number; expense: number }[]; unconverted: number;
}

interface TeacherPayRow {
  teacher_id: string; name: string; pay_type: string | null; pay_rate: number | null;
  pay_currency: string | null; sessions: number; amount: number | null; amount_primary: number | null;
}
interface TeacherPay { primary_currency: string; rows: TeacherPayRow[]; total_primary: number; months: number | null; }

/** Accounting overview, in the school's PRIMARY currency. Each ledger entry is
 *  booked into the primary currency at its transaction-date FX rate; foreign
 *  amounts that couldn't auto-convert surface in "Needs exchange rate". A native
 *  per-currency breakdown is shown alongside. Gated by `accounting.view`. */
@customElement('cadence-accounting-page')
export class CadenceAccountingPage extends LitElement {
  @state() private loading = true;
  @state() private period: Period = 'month';
  @state() private summary: Summary | null = null;
  @state() private entries: any[] = [];
  @state() private categories: any[] = [];
  @state() private showAdd = false;
  @state() private form = { type: 'expense', amount: '', currency: '', date: '', category_id: '', description: '', fx_rate: '' };
  @state() private rateInputs: Record<string, string> = {};
  @state() private teacherPay: TeacherPay | null = null;
  @state() private canExport = false;

  static styles = css`
    :host { display: block; }
    .head { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: var(--space-4); flex-wrap: wrap; }
    h1 { margin: 0; font-size: 1.6rem; }
    .periods { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: var(--space-4); }
    .periods button {
      font: inherit; font-size: 0.85rem; padding: 6px 12px; border-radius: var(--radius-md);
      border: 1px solid var(--color-border); background: var(--color-bg-primary); color: var(--color-text-primary); cursor: pointer;
    }
    .periods button[aria-pressed='true'] { background: var(--color-primary); color: #fff; border-color: var(--color-primary); }
    .cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: var(--space-4); margin-bottom: var(--space-4); }
    .value { font-size: 1.5rem; font-weight: 700; }
    .sub { color: var(--color-text-secondary); font-size: 0.72rem; }
    .value.income { color: var(--color-success); }
    .value.expense { color: var(--color-danger); }
    .value.net.neg { color: var(--color-danger); }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-4); align-items: start; }
    @media (max-width: 820px) { .grid { grid-template-columns: 1fr; } }
    .break { display: flex; flex-direction: column; gap: 6px; }
    .break .row { display: flex; justify-content: space-between; gap: 10px; padding: 6px 0; border-bottom: 1px solid var(--color-border); font-size: 0.9rem; }
    .muted { color: var(--color-text-secondary); }
    .form { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; align-items: end; margin-bottom: var(--space-4); }
    .neg { color: var(--color-danger); }
    .pos { color: var(--color-success); }
    .needs { border: 1px solid var(--color-warning, #d97706); border-radius: var(--radius-md); padding: 14px; margin-bottom: var(--space-4); background: color-mix(in srgb, var(--color-warning, #d97706) 7%, transparent); }
    .needrow { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding: 6px 0; }
    .needrow .desc { flex: 1; min-width: 160px; }
    .needrow input { width: 110px; font: inherit; padding: 7px 9px; border: 1px solid var(--color-border); border-radius: var(--radius-sm); background: var(--color-bg-secondary); color: var(--color-text-primary); }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }

  private range(): { from?: string; to?: string } {
    const now = new Date();
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    const y = now.getFullYear(); const m = now.getMonth();
    if (this.period === 'month') return { from: iso(new Date(y, m, 1)), to: iso(now) };
    if (this.period === 'last') return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) };
    if (this.period === 'year') return { from: iso(new Date(y, 0, 1)), to: iso(now) };
    return {};
  }

  private qs() {
    const { from, to } = this.range();
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    return qs;
  }

  private async load() {
    this.loading = true;
    this.canExport = store.hasPermission('accounting.export');
    const suffix = this.qs().toString() ? `?${this.qs()}` : '';
    try {
      const [summary, entries, categories, teacherPay] = await Promise.all([
        api.get<Summary>(`/accounting/summary${suffix}`),
        api.get<any[]>(`/accounting/entries${suffix}`),
        api.get<any[]>('/accounting/categories'),
        api.get<TeacherPay>(`/accounting/teacher-pay${suffix}`),
      ]);
      this.summary = summary; this.entries = entries; this.categories = categories; this.teacherPay = teacherPay;
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  /** Download a CSV export for the current period. Cookie auth rides along on the
   *  navigation; the server streams it as an attachment. */
  private exportCsv(kind: 'entries' | 'teacher-pay') {
    const suffix = this.qs().toString() ? `?${this.qs()}` : '';
    window.location.href = `/api/v1/accounting/export/${kind}.csv${suffix}`;
  }

  private setPeriod(p: Period) { if (p !== this.period) { this.period = p; this.load(); } }

  private get primary() { return this.summary?.primary_currency || 'USD'; }
  private money(a: number, c = this.primary) { return fmtMoney(a, c); }

  /** Category breakdown in the primary currency (skips entries awaiting conversion). */
  private breakdown(type: 'income' | 'expense') {
    const map = new Map<string, number>();
    for (const e of this.entries) {
      if (e.type !== type || e.amount_primary == null) continue;
      const key = e.category_name || t('accounting.uncategorized');
      map.set(key, (map.get(key) ?? 0) + Number(e.amount_primary));
    }
    return [...map.entries()].sort((a, b) => b[1] - a[1]);
  }

  private get needsRate() { return this.entries.filter((e) => e.amount_primary == null); }

  private async addEntry() {
    const f = this.form;
    if (!f.amount || Number(f.amount) <= 0 || !f.date || !(f.currency || this.primary)) {
      showToast(t('accounting.toast.required'), 'error'); return;
    }
    try {
      await api.post('/accounting/entries', {
        type: f.type, amount: Number(f.amount), currency: (f.currency || this.primary).toUpperCase(),
        date: f.date, category_id: f.category_id || null, description: f.description || null,
        fx_rate: f.fx_rate ? Number(f.fx_rate) : null,
      });
      this.showAdd = false;
      this.form = { type: 'expense', amount: '', currency: '', date: '', category_id: '', description: '', fx_rate: '' };
      showToast(t('accounting.toast.entryAdded'), 'success');
      await this.load();
    } catch (e) { showToast(e instanceof ApiError ? e.message : t('accounting.toast.addFailed'), 'error'); }
  }

  private async saveRate(id: string) {
    const rate = Number(this.rateInputs[id]);
    if (!rate || rate <= 0) { showToast(t('accounting.toast.positiveRate'), 'error'); return; }
    try {
      await api.put(`/accounting/entries/${id}/rate`, { rate });
      this.rateInputs = { ...this.rateInputs, [id]: '' };
      showToast(t('accounting.toast.rateApplied'), 'success');
      await this.load();
    } catch (e) { showToast((e as Error).message, 'error'); }
  }

  render() {
    if (this.loading && !this.summary) return html`<cadence-loading></cadence-loading>`;
    const s = this.summary;
    const today = new Date().toISOString().slice(0, 10);
    const catOptions = this.categories.filter((c) => c.type === this.form.type).map((c) => ({ value: c.id, label: c.name }));
    const formForeign = !!this.form.currency && this.form.currency.toUpperCase() !== this.primary;
    return html`
      <div class="head">
        <h1>${t('accounting.title')} <span class="muted" style="font-size:0.9rem;font-weight:400">· ${this.primary}</span></h1>
        <div class="row" style="gap:8px">
          ${this.canExport ? html`<cadence-button variant="ghost" @click=${() => this.exportCsv('entries')}>${t('accounting.exportCsv')}</cadence-button>` : ''}
          <cadence-button @click=${() => { this.showAdd = !this.showAdd; if (this.showAdd && !this.form.date) this.form = { ...this.form, date: today }; }}>
            ${this.showAdd ? t('accounting.close') : t('accounting.addEntry')}
          </cadence-button>
        </div>
      </div>

      ${this.showAdd ? html`
        <cadence-card heading=${t('accounting.form.heading')}>
          <div class="form">
            <cadence-select label=${t('accounting.form.type')} .value=${this.form.type}
              .options=${[{ value: 'expense', label: t('accounting.form.expense') }, { value: 'income', label: t('accounting.form.income') }]}
              @change=${(e: CustomEvent) => (this.form = { ...this.form, type: e.detail, category_id: '' })}></cadence-select>
            <cadence-input label=${t('accounting.form.amount')} type="number" .value=${this.form.amount} @input=${(e: CustomEvent) => (this.form = { ...this.form, amount: e.detail })}></cadence-input>
            <cadence-currency-select .value=${this.form.currency || this.primary} @change=${(e: CustomEvent) => (this.form = { ...this.form, currency: e.detail })}></cadence-currency-select>
            ${formForeign ? html`<cadence-input label=${t('accounting.form.rateOptional', { primary: this.primary })} type="number" .value=${this.form.fx_rate}
              @input=${(e: CustomEvent) => (this.form = { ...this.form, fx_rate: e.detail })}></cadence-input>` : ''}
            <cadence-input label=${t('accounting.form.date')} type="date" .value=${this.form.date} @input=${(e: CustomEvent) => (this.form = { ...this.form, date: e.detail })}></cadence-input>
            <cadence-select label=${t('accounting.form.category')} placeholder=${t('accounting.form.categoryNone')} .value=${this.form.category_id}
              .options=${catOptions} @change=${(e: CustomEvent) => (this.form = { ...this.form, category_id: e.detail })}></cadence-select>
            <cadence-input label=${t('accounting.form.description')} .value=${this.form.description} @input=${(e: CustomEvent) => (this.form = { ...this.form, description: e.detail })}></cadence-input>
            <cadence-button @click=${() => this.addEntry()}>${t('common.actions.save')}</cadence-button>
          </div>
          ${formForeign ? html`<p class="muted" style="margin:0">${t('accounting.form.autoFetchHint')}</p>` : ''}
        </cadence-card>
      ` : ''}

      <div class="periods">
        ${([['month', t('accounting.periods.month')], ['last', t('accounting.periods.last')], ['year', t('accounting.periods.year')], ['all', t('accounting.periods.all')]] as [Period, string][]).map(
          ([p, label]) => html`<button aria-pressed=${this.period === p ? 'true' : 'false'} @click=${() => this.setPeriod(p)}>${label}</button>`,
        )}
      </div>

      <div class="cards">
        <cadence-stat-card heading=${t('accounting.cards.income')} icon="trending-up"><div class="value income">${this.money(s?.income ?? 0)}</div></cadence-stat-card>
        <cadence-stat-card heading=${t('accounting.cards.expenses')} icon="trending-down"><div class="value expense">${this.money(s?.expense ?? 0)}</div></cadence-stat-card>
        <cadence-stat-card heading=${t('accounting.cards.net')} icon="chart-line"><div class="value net ${(s?.net ?? 0) < 0 ? 'neg' : ''}">${this.money(s?.net ?? 0)}</div></cadence-stat-card>
        <cadence-stat-card heading=${t('accounting.cards.outstanding')} icon="clock"><div class="value">${this.money(s?.outstanding ?? 0)}</div>${s?.outstanding_indicative ? html`<div class="sub">${t('accounting.cards.indicative')}</div>` : ''}</cadence-stat-card>
      </div>

      ${this.needsRate.length ? html`
        <div class="needs">
          <strong>⚠ ${this.needsRate.length === 1 ? t('accounting.fx.needsRateOne', { n: this.needsRate.length }) : t('accounting.fx.needsRateMany', { n: this.needsRate.length })}</strong>
          <p class="muted" style="margin:4px 0 10px">${t('accounting.fx.needsRateHelp', { primary: this.primary })}</p>
          ${this.needsRate.map((e) => html`
            <div class="needrow">
              <span class="desc">${(e.date ?? '').slice(0, 10)} · ${e.description ?? e.category_name ?? e.type} · <strong>${this.money(e.amount, e.currency)}</strong></span>
              <span class="muted">${t('accounting.fx.rateEquals', { currency: e.currency })}</span>
              <input type="number" placeholder=${t('accounting.fx.ratePlaceholder')} .value=${this.rateInputs[e.id] ?? ''} @input=${(ev: Event) => (this.rateInputs = { ...this.rateInputs, [e.id]: (ev.target as HTMLInputElement).value })} />
              <span class="muted">${this.primary}</span>
              <cadence-button variant="ghost" @click=${() => this.saveRate(e.id)}>${t('accounting.fx.apply')}</cadence-button>
            </div>`)}
        </div>` : ''}

      <div class="grid" style="margin-bottom:var(--space-4)">
        <cadence-card heading=${t('accounting.breakdown.incomeByCategory')}>
          ${this.breakdown('income').length
            ? html`<div class="break">${this.breakdown('income').map(([name, total]) => html`<div class="row"><span>${name}</span><span class="pos">${this.money(total)}</span></div>`)}</div>`
            : html`<p class="muted">${t('accounting.breakdown.noIncome')}</p>`}
        </cadence-card>
        <cadence-card heading=${t('accounting.breakdown.expensesByCategory')}>
          ${this.breakdown('expense').length
            ? html`<div class="break">${this.breakdown('expense').map(([name, total]) => html`<div class="row"><span>${name}</span><span class="neg">${this.money(total)}</span></div>`)}</div>`
            : html`<p class="muted">${t('accounting.breakdown.noExpenses')}</p>`}
        </cadence-card>
      </div>

      ${s && s.by_currency.length > 1 ? html`
        <cadence-card heading=${t('accounting.breakdown.byCurrency')} style="display:block;margin-bottom:var(--space-4)">
          <div class="break">
            ${s.by_currency.map((c) => html`<div class="row"><span>${c.currency}</span><span><span class="pos">+${c.income.toFixed(2)}</span> / <span class="neg">−${c.expense.toFixed(2)}</span></span></div>`)}
          </div>
          <p class="muted" style="margin:8px 0 0">${t('accounting.breakdown.byCurrencyNote')}</p>
        </cadence-card>` : ''}

      ${this.teacherPay && this.teacherPay.rows.length ? html`
        <cadence-card heading=${t('accounting.teacherPay.heading')} style="display:block;margin-bottom:var(--space-4)">
          ${this.teacherPay.months == null ? html`<p class="muted" style="margin-top:0">${t('accounting.teacherPay.boundedHint')}</p>` : ''}
          <cadence-table .columns=${[
            { key: 'name', label: t('accounting.teacherPay.teacher') },
            { key: 'pay_type', label: t('accounting.teacherPay.basis'), render: (r: TeacherPayRow) => html`${r.pay_type === 'per_class' ? t('accounting.teacherPay.perClass') : r.pay_type === 'monthly_salary' ? t('accounting.teacherPay.monthly') : html`<span class="muted">${t('accounting.teacherPay.notSet')}</span>`}` },
            { key: 'sessions', label: t('accounting.teacherPay.sessions'), render: (r: TeacherPayRow) => html`${r.pay_type === 'per_class' ? r.sessions : '—'}` },
            { key: 'pay_rate', label: t('accounting.teacherPay.rate'), render: (r: TeacherPayRow) => r.pay_rate == null ? html`<span class="muted">—</span>` : html`${this.money(r.pay_rate, r.pay_currency || this.primary)}` },
            { key: 'amount', label: t('accounting.teacherPay.pay'), render: (r: TeacherPayRow) => r.amount == null ? html`<span class="muted">—</span>` : html`${this.money(r.amount, r.pay_currency || this.primary)}` },
            { key: 'amount_primary', label: t('accounting.teacherPay.inPrimary', { primary: this.primary }), render: (r: TeacherPayRow) => (r.pay_currency || this.primary) === this.primary ? html`<span class="muted">—</span>` : (r.amount_primary == null ? html`<span class="neg">${t('accounting.teacherPay.needsRate')}</span>` : html`${this.money(r.amount_primary)}`) },
          ]} .rows=${this.teacherPay.rows}></cadence-table>
          <div class="row" style="justify-content:space-between;align-items:center;margin-top:10px">
            <span class="muted">${t('accounting.teacherPay.estimatedTotal')} <strong>${this.money(this.teacherPay.total_primary)}</strong></span>
            ${this.canExport ? html`<cadence-button variant="ghost" @click=${() => this.exportCsv('teacher-pay')}>${t('accounting.teacherPay.export')}</cadence-button>` : ''}
          </div>
        </cadence-card>` : ''}

      <cadence-card heading=${t('accounting.ledger.heading')}>
        ${this.entries.length
          ? html`<cadence-table .columns=${[
              { key: 'date', label: t('accounting.ledger.date') },
              { key: 'type', label: t('accounting.ledger.type'), render: (r: any) => html`<cadence-badge color=${r.type === 'income' ? 'var(--color-success)' : 'var(--color-danger)'}>${r.type}</cadence-badge>` },
              { key: 'category_name', label: t('accounting.ledger.category'), render: (r: any) => html`${r.category_name ?? '—'}` },
              { key: 'description', label: t('accounting.ledger.description'), render: (r: any) => html`${r.description ?? (r.payment_id ? t('accounting.ledger.studentPayment') : '—')}` },
              { key: 'amount', label: t('accounting.ledger.amount'), render: (r: any) => html`<span class=${r.type === 'income' ? 'pos' : 'neg'}>${r.type === 'income' ? '+' : '−'}${this.money(r.amount, r.currency)}</span>` },
              { key: 'amount_primary', label: t('accounting.ledger.inPrimary', { primary: this.primary }), render: (r: any) => r.currency === this.primary ? html`<span class="muted">—</span>` : (r.amount_primary == null ? html`<span class="neg">${t('accounting.ledger.needsRate')}</span>` : html`${this.money(r.amount_primary)}`) },
            ]} .rows=${this.entries}></cadence-table>`
          : html`<cadence-empty-state heading=${t('accounting.ledger.emptyHeading')} message=${t('accounting.ledger.emptyMessage')}></cadence-empty-state>`}
      </cadence-card>
    `;
  }
}
